import React, { useState } from 'react';

import { getMaturityLabel } from '../../utils/assessmentFormConfig';
import type { AssessmentFormUiConfig } from '../../utils/assessmentFormConfig';
import { cn } from '../../utils/cn';

export type AssessmentMaturityPickerProps = {
  name: string;
  value: string;
  disabled?: boolean;
  formConfig: AssessmentFormUiConfig;
  onChange: (stateKey: string) => void;
};

export function AssessmentMaturityPicker({
  name,
  value,
  disabled = false,
  formConfig,
  onChange,
}: AssessmentMaturityPickerProps) {
  const keys = formConfig.maturityStateKeys;

  const stepCount = Math.max(keys.length, 1);

  const selectedIndex = keys.findIndex((key) => key === value);
  const selectedKey = selectedIndex >= 0 ? keys[selectedIndex] : null;
  const selectedLabel = selectedKey ? getMaturityLabel(selectedKey, formConfig) : '';
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const displayIndex =
    hoveredIndex ?? (selectedIndex >= 0 ? selectedIndex : -1);
  const displayKey = displayIndex >= 0 ? keys[displayIndex] : null;
  const displayHint = displayKey ? formConfig.stateDescriptions[displayKey] : '';
  const displayLabel = displayKey ? getMaturityLabel(displayKey, formConfig) : '';
  const isHintPreview =
    hoveredIndex != null &&
    (selectedIndex < 0 || hoveredIndex !== selectedIndex);

  const hintLead =
    !isHintPreview && selectedIndex >= 0
      ? `Selected · ${selectedIndex + 1} ${selectedLabel}`
      : displayIndex >= 0
        ? `${displayIndex + 1} ${displayLabel}`
        : '';

  return (
    <div
      className="asses-maturity-picker-wrap"
      onMouseLeave={() => setHoveredIndex(null)}
    >
      <div
        className="asses-maturity-picker"
        role="radiogroup"
        aria-label={name}
        style={{ '--maturity-steps': stepCount } as React.CSSProperties}
      >
        {keys.map((stateKey, index) => {
          const selected = value === stateKey;
          const label = getMaturityLabel(stateKey, formConfig);
          const hint = formConfig.stateDescriptions[stateKey];

          return (
            <button
              key={stateKey}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              title={hint}
              className={cn(
                'asses-maturity-option',
                selected && 'is-selected',
                selectedIndex >= 0 && index < selectedIndex && 'is-preceding',
                hoveredIndex === index && 'is-hovered'
              )}
              style={
                {
                  '--maturity-step-index': index + 1,
                  '--maturity-step-count': stepCount,
                } as React.CSSProperties
              }
              onClick={() => onChange(stateKey)}
              onMouseEnter={() => {
                if (!disabled) {
                  setHoveredIndex(index);
                }
              }}
            >
              <span className="asses-maturity-option__body">
                <span className="asses-maturity-option__num">{index + 1}</span>
                <span className="asses-maturity-option__label">{label}</span>
              </span>
            </button>
          );
        })}
      </div>
      {displayKey && displayHint ? (
        <div
          className={cn(
            'asses-maturity-hint',
            isHintPreview && 'asses-maturity-hint--preview'
          )}
          role="status"
        >
          <p className="asses-maturity-hint__lead">{hintLead}</p>
          <p className="asses-maturity-hint__text">{displayHint}</p>
        </div>
      ) : null}
    </div>
  );
}

export default AssessmentMaturityPicker;
